'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatReportValue } from '@/app/reports/components/report-chart-utils';
import { ReportEmptyState } from '@/app/reports/components/ReportEmptyState';
import {
  preferReadableNameColumns,
  resolveLookupLabel,
  type ReportLookupMaps,
} from '@/app/reports/hooks/useReportLookupMaps';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type TableColumn = {
  key: string;
  labelKey: string;
  format?: 'text' | 'number' | 'money' | 'percent' | 'date';
};

type ReportDataTableProps = {
  rows: Record<string, string | number | null>[];
  columns?: TableColumn[];
  currency?: string;
  lookupMaps?: ReportLookupMaps;
};

const EMPTY_MAPS: ReportLookupMaps = { storesById: {}, processorsById: {} };

const formatCell = (
  value: string | number | null,
  format: TableColumn['format'],
  currency?: string,
) => {
  if (value === null || value === undefined || value === '') return '—';
  if (format === 'money' && typeof value === 'number') return formatReportValue(value, currency);
  if (format === 'percent' && typeof value === 'number') return formatReportValue(value, undefined, true);
  if (format === 'number' && typeof value === 'number') return value.toLocaleString();
  return String(value);
};

export function ReportDataTable({ rows, columns, currency, lookupMaps = EMPTY_MAPS }: ReportDataTableProps) {
  const { t } = useTranslation('reports');
  const resolvedColumns = useMemo((): TableColumn[] => {
    const base: TableColumn[] =
      columns ??
      (rows[0]
        ? Object.keys(rows[0]).map((key) => ({ key, labelKey: key, format: 'text' as const }))
        : []);
    return preferReadableNameColumns(base);
  }, [columns, rows]);

  if (!rows.length) {
    return <ReportEmptyState variant="table" />;
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {resolvedColumns.map((col) => (
              <TableHead key={col.key}>{t(col.labelKey, { defaultValue: col.key })}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {resolvedColumns.map((col) => {
                const raw = row[col.key] ?? null;
                const display = resolveLookupLabel(col.key, raw, lookupMaps);
                return (
                  <TableCell key={col.key} className="max-w-[280px] truncate">
                    {formatCell(display, col.format, currency)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
